/**
 * Protected Route Components
 *
 * - ProtectedRoute: Requires authentication
 * - RequireOnboarding: Requires completed onboarding
 * - BlockIfOnboardingComplete: Prevents access if onboarding is done
 *
 * All redirects use next= query param for return URL.
 */

import { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { isOnboardingComplete } from '@/types/profile';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Requires user to be authenticated.
 * Redirects to login with next= param if not.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const hasResolvedRef = useRef(false);

  if (!isLoading) hasResolvedRef.current = true;

  // Only show spinner on cold start — skip during in-app navigation re-evals
  if (isLoading && !hasResolvedRef.current) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with next= query param
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signup?next=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Requires user to have completed onboarding.
 * Redirects to /login with next= if not authenticated.
 * Redirects to /onboarding with next= if onboarding incomplete.
 */
export function RequireOnboarding({ children }: ProtectedRouteProps) {
  const { isAuthenticated, profile, isLoading } = useAuth();
  const location = useLocation();
  const hasResolvedRef = useRef(false);

  if (!isLoading && profile) hasResolvedRef.current = true;

  // Only show spinner on cold start — skip during in-app navigation re-evals
  if ((isLoading || (isAuthenticated && !profile)) && !hasResolvedRef.current) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signup?next=${returnUrl}`} replace />;
  }

  // Check if onboarding is completed using field-based check
  if (!isOnboardingComplete(profile)) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/onboarding?next=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Blocks access if user has already completed onboarding.
 * Used to prevent re-entering onboarding flow.
 * Requires authentication.
 */
export function BlockIfOnboardingComplete({ children }: ProtectedRouteProps) {
  const { isAuthenticated, profile, isLoading } = useAuth();
  const location = useLocation();

  // Wait while auth is loading OR while profile hasn't loaded yet
  if (isLoading || (isAuthenticated && !profile)) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signup?next=${returnUrl}`} replace />;
  }

  // If onboarding is complete, redirect to app
  if (isOnboardingComplete(profile)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Redirects authenticated users away from auth pages.
 * Used for login/signup when user is already logged in.
 * Preserves ?next= query param through the redirect chain.
 */
export function RedirectIfAuthenticated({ children }: ProtectedRouteProps) {
  const { isAuthenticated, profile, isLoading } = useAuth();
  const location = useLocation();

  // Wait while auth is loading OR while profile hasn't loaded yet (need profile to decide redirect target)
  if (isLoading || (isAuthenticated && !profile)) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Preserve the ?next= param from the current URL
    const params = new URLSearchParams(location.search);
    const next = params.get('next') || '/app/dashboard';

    // If onboarding not complete, go there (with next param)
    if (!isOnboardingComplete(profile)) {
      return <Navigate to={`/onboarding?next=${encodeURIComponent(next)}`} replace />;
    }
    // Otherwise go to intended destination
    return <Navigate to={next} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
